import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Users, Briefcase, Clock, Zap, Shield,
  Layers, Mail, Sparkles,
} from "lucide-react";

const FEATURES = [
  { icon: <Users size={20} />, title: "Clients as a first-class citizen",
    body: "A real CRM tuned for accounting firms: contacts, notes, status, custom fields — not a glorified spreadsheet." },
  { icon: <Briefcase size={20} />, title: "Jobs that move themselves",
    body: "Templated workflows + drag-and-drop kanban. Status changes trigger reminders, hand-offs, and client requests automatically." },
  { icon: <Clock size={20} />, title: "Time that closes the loop",
    body: "Live stopwatch on every job. Billable vs non-billable. Realization and utilization reports without the spreadsheet wrangling." },
  { icon: <Mail size={20} />, title: "Email triage, not Email overload",
    body: "Connect Gmail or Outlook and route every reply to the right client and job. Convert any email into a task in one click." },
  { icon: <Sparkles size={20} />, title: "AI agents that do the boring parts",
    body: "Bookkeeper, follow-up bot, period-close copilot — they propose, you approve. Never auto-execute on financial data." },
  { icon: <Shield size={20} />, title: "Built like enterprise from day one",
    body: "Multi-tenant, RBAC, audit log, SOC2-track. Field-level encryption on PII. WCAG 2.1 AA accessible." },
];

const STATS = [
  { value: "16", label: "Modules at parity" },
  { value: "$28K", label: "Built solo with AI tools" },
  { value: "6 mo", label: "From zero to private beta" },
  { value: "1", label: "Place for the whole firm" },
];

const TRUST = [
  "Multi-tenant by default",
  "SOC2 Type I track",
  "GDPR + CCPA + IRS Pub 4557",
  "Audit log on every record",
];

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <header className="border-b border-[var(--border)] bg-[var(--bg)] sticky top-0 z-30 backdrop-blur" data-testid="landing-header">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center font-heading italic text-lg"
                 style={{ background: "var(--ink)", color: "var(--paper)", borderRadius: "50%" }}>p</div>
            <div className="font-heading text-[18px]" style={{ fontWeight: 500 }}>Practice OS</div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-[14px]">
            <a href="#features" className="text-[var(--ink-2)] hover:text-[var(--ink)] transition" data-testid="landing-nav-features">Features</a>
            <a href="#why" className="text-[var(--ink-2)] hover:text-[var(--ink)] transition" data-testid="landing-nav-why">Why now</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="btn-ghost" data-testid="landing-cta-login">Sign in</Link>
            <Link to="/register" className="btn-primary" data-testid="landing-cta-register-top">
              Start free <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="paper-noise border-b border-[var(--border)]" data-testid="landing-hero">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="badge badge-outline">For Accounting Firms · 2026</span>
              <span className="overline">Private beta open</span>
            </div>
            <h1 className="font-display text-[56px] md:text-[88px] leading-[0.95] tracking-tight text-[var(--ink)]">
              Run your <em className="italic" style={{ color: "var(--accent)" }}>entire firm</em><br />
              from one place.
            </h1>
            <p className="mt-10 text-xl text-[var(--ink-2)] leading-[1.55] max-w-2xl">
              Clients, jobs, time, email, workflows — stitched together with AI that helps
              your team move faster without ever auto-touching a number.
              <em className="italic"> No more swivel-chairing between five tools.</em>
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-3">
              <Link to="/register" className="btn-primary" data-testid="landing-cta-register-hero">
                Start a free trial <ArrowRight size={14} />
              </Link>
              <Link to="/login" className="btn-secondary" data-testid="landing-cta-login-hero">
                Sign in
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-6 gap-y-2 text-[12px] text-[var(--ink-3)]">
              {TRUST.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={12} className="text-[var(--moss)]" /> {t}
                </span>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4 lg:pl-8 lg:border-l border-[var(--border)]">
            <div className="overline mb-3">By the numbers</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-8">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-4xl text-[var(--ink)] tabular">{s.value}</div>
                  <div className="text-[12px] text-[var(--ink-3)] mt-1.5 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="border-b border-[var(--border)]" data-testid="landing-features">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24">
          <div className="overline mb-3">§ Features</div>
          <h2 className="font-display text-4xl md:text-5xl mb-3">
            The whole firm, in one workspace.
          </h2>
          <p className="text-lg text-[var(--ink-2)] max-w-2xl mb-14">
            Six surfaces, one source of truth. Each one tuned for how accounting firms actually work.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="card p-7" data-testid={`landing-feature-${f.title.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center gap-2 text-[var(--accent)] mb-4">{f.icon}</div>
                <h3 className="font-heading text-xl text-[var(--ink)] mb-2 leading-tight" style={{ fontWeight: 500 }}>{f.title}</h3>
                <p className="text-[14px] text-[var(--ink-2)] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why now */}
      <section id="why" className="border-b border-[var(--border)] bg-[var(--paper)]" data-testid="landing-why">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-3">§ Why now</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">
              The category was built in <em className="italic" style={{ color: "var(--accent)" }}>2015</em>.
              <br />Yours doesn&apos;t have to be.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <Block icon={<Zap size={18} />} title="AI is the new pricing pressure"
                   body="Bookkeepers and tax preparers are pricing transactional work like commodities. Practice OS gives your team the leverage to stay advisors, not data-entry clerks." />
            <Block icon={<Layers size={18} />} title="Tool sprawl is killing partners"
                   body="The median firm uses 7–11 SaaS tools to run a single client. Each new tool is one more place your team forgets to check. We end the sprawl, one workflow at a time." />
            <Block icon={<Shield size={18} />} title="Trust beats features"
                   body="Accounting data is the most sensitive a client owns. We're built multi-tenant, RBAC-enforced, audit-logged from day zero — not retrofitted after a breach." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[var(--border)]" data-testid="landing-cta-band">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="overline mb-4">Open beta · Q1 2026</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] max-w-3xl mx-auto">
            Spend less time chasing, <em className="italic" style={{ color: "var(--accent)" }}>more time advising</em>.
          </h2>
          <p className="text-lg text-[var(--ink-2)] mt-6 max-w-xl mx-auto">
            Spin up a workspace in 60 seconds. We&apos;ll seed it with sample clients and jobs so you can click around.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="btn-primary" data-testid="landing-cta-register-bottom">
              Start a free trial <ArrowRight size={14} />
            </Link>
            <Link to="/login" className="btn-secondary" data-testid="landing-cta-login-bottom">Sign in</Link>
          </div>
          <div className="mt-6 text-[12px] text-[var(--ink-3)]">
            Try the demo:{" "}
            <code className="kbd">owner@northwood.example.com</code>
            {" "}/{" "}
            <code className="kbd">Demo1234!</code>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-14" data-testid="landing-footer">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
          <div>
            <div className="w-11 h-11 flex items-center justify-center font-heading italic text-lg"
                 style={{ background: "var(--ink)", color: "var(--paper)", borderRadius: "50%" }}>p</div>
            <div className="font-heading text-xl mt-3" style={{ fontWeight: 500 }}>Practice OS</div>
            <div className="text-[13px] text-[var(--ink-2)] mt-1">Built by a solo founder with AI tools.</div>
          </div>
          <div>
            <div className="overline mb-3">Product</div>
            <div className="space-y-2 text-[13px] text-[var(--ink-2)]">
              <a href="#features" className="block hover:text-[var(--ink)]">Features</a>
              <Link to="/login" className="block hover:text-[var(--ink)]">Sign in</Link>
              <Link to="/register" className="block hover:text-[var(--ink)]">Create a firm</Link>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Trust & Legal</div>
            <p className="text-[13px] text-[var(--ink-2)] leading-relaxed">
              Multi-tenant, SOC2 Type I track, GDPR &amp; CCPA aware, IRS Pub 4557 informed.
              This is a demo environment — not production financial software. Not investment, legal, or tax advice.
            </p>
          </div>
        </div>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 mt-12 pt-6 border-t border-[var(--border)] flex flex-wrap justify-between text-[12px] text-[var(--ink-3)] font-mono">
          <div>© 2026 Emergent · Practice OS</div>
          <div>v1.0 · Made with Emergent</div>
        </div>
      </footer>
    </div>
  );
}

function Block({ icon, title, body }) {
  return (
    <div className="border-b border-[var(--border)] pb-6 last:border-0 last:pb-0">
      <div className="flex items-center gap-2 text-[var(--accent)] mb-2">{icon}
        <div className="font-medium text-[var(--ink)] text-[15px]">{title}</div>
      </div>
      <p className="text-[15px] text-[var(--ink-2)] leading-relaxed">{body}</p>
    </div>
  );
}
